import {
  hasLoadedAlignmentInProject,
  useApiServices,
  useDataContext,
  useMapTileConfig,
  useCapabilityRegistryContext,
  useThemeContext,
  useProjectSettings,
  useFeatureExtractionContext,
  getVisiblePointCloudIds,
  viewerPreferenceMaterialPhong,
} from '@mobile-mapping/providers';
import { CadEditingCapability } from './feature-extraction/CadEditingCapability';
import { useCadEditingPane } from './feature-extraction/useCadEditingPane';
import { fetchGeoDataFilesByCsib } from '@data-access/coordinates';
import {
  PaneContents,
  ViewMode,
  ViewModeCapabilityCoordinator,
} from '@mobile-mapping/web3d';
import { Web3DViewer } from '@technology/web3d';
import { useKeyDownEvent } from '@solid-primitives/keyboard';
import { PaneElementProps } from '@trimble/pane-layout';
import { useHover, useLogger } from '@trimble/solidjs-utils';
import { Web3DViewerWrapper } from '@trimble/utilities';
import { CapabilitiesCollection } from '@trimble/web3d';
import {
  ImageCapabilityBase,
  ImagePointcloudCapability,
  MarkupCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import {
  MarkupsCapabilityProvider,
  PointCloudCapabilityProvider,
  StationsCapabilityProvider,
  TrimBimCapabilityProvider,
} from '@trimble/web3d-providers';
import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';

import { useStationLoadContext } from './station-load/station-load-context';

import {
  EmptyViewerState,
  EmptyViewerStateProps,
} from './components/EmptyViewerState';
import { PaneViewerChrome } from './components/PaneViewerChrome';
import { applyViewerPreferences } from '@trimble/viewer-preferences';
import {
  createPaneViewerSession,
  type PaneViewerSession,
} from './pane-viewer-session';
import { usePaneViewModeBridge } from './hooks/usePaneViewModeBridge';

export const PaneViewer = (
  props: PaneElementProps<PaneContents, ViewMode>
): JSX.Element => {
  const logger = useLogger().getLogger('PaneViewer');

  const [viewModeCapCoord, setViewModeCapCoord] =
    createSignal<ViewModeCapabilityCoordinator>();

  const [emptyViewerStateProps, setEmptyViewerStateProps] =
    createSignal<EmptyViewerStateProps>();

  let session: PaneViewerSession | undefined;

  /* eslint-disable solid/reactivity -- the hook tracks layoutConfig.layouts in memos and effects */
  const { viewModeFromLayout } = usePaneViewModeBridge({
    layoutConfig: props.layoutConfig,
    viewModeCapCoord,
  });
  /* eslint-enable solid/reactivity */

  const { web3dSettings, preferenceSettings } = useThemeContext();
  const {
    dataLoadingService,
    dataState,
    currentLoadedCsib,
    currentLoadedCenter,
    currentLoadedBounds,
    loadedCsibs,
    loadedCenters,
  } = useDataContext();
  const { formattingSettings } = useProjectSettings();
  const { tilesetPrefetchContext } = useStationLoadContext();

  let placeholder!: HTMLDivElement;
  let viewerWrapper: Web3DViewerWrapper | undefined;

  const { imagingApi, coordinatesClient } = useApiServices();
  const mapTileConfig = useMapTileConfig();

  const [viewer, setViewer] = createSignal<Web3DViewer>();

  const viewModeType = createMemo(() => viewModeCapCoord()?.viewMode().type);

  const featureExtraction = useFeatureExtractionContext();
  const featureExtractionLogger = useLogger().getLogger('FeatureExtraction');

  // Only CSIB-bearing clouds — no cross-cloud CSIB substitution.
  const pointCloudRoots = createMemo(() => {
    const csibs = loadedCsibs();
    const centers = loadedCenters();
    return (getVisiblePointCloudIds(dataState) ?? []).flatMap((id) => {
      const csib = csibs.get(id);
      const center = centers.get(id);
      return csib && center ? [{ id, csib, center }] : [];
    });
  });

  const sceneCsib = createMemo(() => {
    const roots = pointCloudRoots();
    if (roots.length === 0) {
      return undefined;
    }
    const primary = [...roots].sort((a, b) => a.id.localeCompare(b.id))[0];
    return primary?.csib;
  });

  const allowMarkups = createMemo(() => {
    return viewModeType() !== '2D';
  });

  const capabilityRegistry = useCapabilityRegistryContext().capabilityRegistry;
  /* eslint-disable solid/reactivity -- rowIdx/cellIdx are static for the pane's lifetime */
  const capRegContainer = capabilityRegistry.registerPaneContainer(
    undefined,
    props.layoutConfig.rowIdx,
    props.layoutConfig.cellIdx
  );
  /* eslint-enable solid/reactivity */

  const { cadEditingSession } = useCadEditingPane({
    featureExtraction,
    featureExtractionLogger,
    isEditingEligible: featureExtraction.isEditingEligible,
    viewModeType,
    sceneCsib,
    pointCloudRoots,
    getViewerWrapper: () => {
      return viewer() === undefined ? undefined : viewerWrapper;
    },
    fetchGeoDataFiles: (csib64) =>
      fetchGeoDataFilesByCsib(coordinatesClient, csib64),
    onCadEditingEnabled: () => {
      const capability = capRegContainer.get(CadEditingCapability)();
      capability?.enable();
      capability?.activate();
    },
    onCadEditingDisabled: () => {
      void capRegContainer.get(CadEditingCapability)()?.disable();
    },
    onRequestDrawingTool: (tool) => {
      capRegContainer.get(CadEditingCapability)()?.toggleTool(tool);
    },
  });

  const allowMarkupEditing = createMemo(
    () =>
      viewModeType() === '3D' &&
      capRegContainer
        .get(StationsNavigationCapability)()
        ?.selectedStationIndex() == null
  );

  const markupCapability = (): MarkupCapability | undefined =>
    capabilityRegistry.get(MarkupCapability)();

  const isInStationNavigation = createMemo(
    () =>
      viewModeCapCoord()
        ?.stationNavigationController()
        ?.selectedStationIndex() != null
  );

  createEffect(
    on(allowMarkupEditing, (allowed) => {
      if (allowed) {
        return;
      }
      const mc = markupCapability();
      if (!mc?.isActive()) {
        return;
      }
      capabilityRegistry
        .get(MarkupsCapabilityProvider)()
        ?.discardPendingMarkups();
      mc.deactivate();
    })
  );

  const isAlignmentMeasurementInteractionAllowed = createMemo(() => {
    if (viewModeType() !== '3D') {
      return false;
    }
    return hasLoadedAlignmentInProject(dataState);
  });

  const isDrawMarkupCreationAllowed = createMemo(() => {
    if (viewModeType() !== '3D') {
      return false;
    }
    const pcProvider = capRegContainer.get(PointCloudCapabilityProvider)();
    if (!pcProvider) {
      return false;
    }
    return pcProvider.items().length > 0 && pcProvider.isPointCloudsVisible();
  });

  const { isHovered, ref: hoverRef } = useHover();
  const keyDownEvent = useKeyDownEvent();

  // Component-scope + keyed on the viewer signal so it re-runs for every pane when
  // the preferences change.
  createEffect(() => {
    const activeViewer = viewer();
    if (!activeViewer) return;

    applyViewerPreferences(
      activeViewer.settings,
      preferenceSettings(),
      viewerPreferenceMaterialPhong
    );
  });

  onMount(() => {
    session = createPaneViewerSession({
      placeholder,
      web3DPath: props.web3DPath,
      capRegContainer,
      setViewer,
      isAlignmentMeasurementInteractionAllowed,
      isDrawMarkupCreationAllowed,
      logger,
      cadEditingSession,
      clearDesiredDrawingTool: () =>
        featureExtraction.setDesiredDrawingTool(undefined),
      allowMarkups,
      allowMarkupEditing,
      capabilityRegistry,
      viewMode: {
        initial: viewModeFromLayout(),
        dataLoadingService,
        web3dSettings,
        fetchGeoDataFiles: (csib64) =>
          fetchGeoDataFilesByCsib(coordinatesClient, csib64),
        datasetCsib: currentLoadedCsib,
        datasetCenter: currentLoadedCenter,
        datasetBounds: currentLoadedBounds,
        mapTileConfig,
      },
      runtime: {
        isInStationNavigation,
        viewModeType,
        isHovered,
        keyDownEvent,
        imagingApi,
        tilesetPrefetchContext,
      },
      registerSyncedPane: props.registerSyncedPane,
    });
    viewerWrapper = session.viewerWrapper;
    const viewModeCoordinator = session.viewModeCoordinator;
    batch(() => {
      setViewModeCapCoord(viewModeCoordinator);
      setEmptyViewerStateProps({
        viewMode: () => viewModeCoordinator.viewMode(),
        cameraIndex: () => viewModeCoordinator.currentCameraIndex(),
        datasetCsib: currentLoadedCsib,
        pcProvider: () => capRegContainer.get(PointCloudCapabilityProvider)(),
        trbProvider: () => capRegContainer.get(TrimBimCapabilityProvider)(),
        imagePointcloudCap: () =>
          capRegContainer.get(ImagePointcloudCapability)(),
        imageCaps: () =>
          capRegContainer.get(CapabilitiesCollection<ImageCapabilityBase>)(),
        stationsProvider: () =>
          capabilityRegistry.get(StationsCapabilityProvider)(),
        backgroundTopColor: () => preferenceSettings().backgroundTopColor,
        backgroundBottomColor: () => preferenceSettings().backgroundBottomColor,
      });
    });
  });

  onCleanup(() => {
    session?.cleanup();
  });

  return (
    <div class="relative w-full h-full overflow-hidden group" ref={hoverRef}>
      <div
        class="absolute inset-0 w-full h-full overflow-hidden"
        ref={(x) => {
          placeholder = x;
        }}
      />
      <Show when={emptyViewerStateProps()}>
        <EmptyViewerState
          {...emptyViewerStateProps()!}
          class="absolute inset-0 z-content"
        />
      </Show>

      <PaneViewerChrome
        viewer={viewer}
        viewModeType={viewModeType}
        viewModeCapCoord={viewModeCapCoord}
        capRegContainer={capRegContainer}
        allowPopup={props.allowPopup}
        formattingSettings={formattingSettings}
        planarContents={() => props.data.planarContents()}
      />
    </div>
  );
};
